import React, { useRef, useState } from "react";
import { Button, Spinner } from "react-bootstrap";
import { FaCloudUploadAlt, FaTimes } from "react-icons/fa";
import { toast } from "react-toastify";
import { resolveAssetUrl } from "../utils/assets";
import "./CmsImageField.css";

const isBlobUrl = (url) => typeof url === "string" && url.startsWith("blob:");

/**
 * CMS image picker. Files stay local (blob preview) until the parent saves
 * and uploads them — avoids orphan files when the admin never clicks Save.
 */
const CmsImageField = ({
  label,
  value,
  onChange,
  onPendingFile,
  helpText = "Uploads and removals are applied only after you click Save for this section.",
}) => {
  const inputRef = useRef(null);
  const [selecting, setSelecting] = useState(false);
  const preview = value ? resolveAssetUrl(value) || value : "";

  const handleFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    const file = files[0];
    if (!file.type?.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }

    setSelecting(true);
    try {
      if (isBlobUrl(value)) {
        URL.revokeObjectURL(value);
      }
      const blobUrl = URL.createObjectURL(file);
      onPendingFile?.(file);
      onChange(blobUrl);
      toast.success("Image selected — click Save to keep it");
    } finally {
      setSelecting(false);
    }
  };

  const clear = () => {
    if (isBlobUrl(value)) {
      URL.revokeObjectURL(value);
    }
    onPendingFile?.(null);
    onChange("");
    toast.info("Image removed — click Save to apply");
  };

  return (
    <div className="cms-image-field">
      {label && <label className="form-label">{label}</label>}
      <div className="cms-image-row">
        <div className="cms-image-preview">
          {preview ? (
            <img src={preview} alt="" />
          ) : (
            <span>No image</span>
          )}
        </div>
        <div className="cms-image-actions">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            hidden
            onChange={handleFiles}
          />
          <Button
            type="button"
            variant="outline-dark"
            size="sm"
            disabled={selecting}
            onClick={() => inputRef.current?.click()}
          >
            {selecting ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <FaCloudUploadAlt /> Upload
              </>
            )}
          </Button>
          {value && (
            <Button type="button" variant="outline-danger" size="sm" onClick={clear}>
              <FaTimes /> Remove
            </Button>
          )}
        </div>
      </div>
      {helpText && <div className="form-text">{helpText}</div>}
    </div>
  );
};

export default CmsImageField;
